import { memo } from 'react'
import { safeButtonHaptic } from '../../lib/safeTelegram'

/**
 * Figma Button Component — 4 варианта
 *
 * Props:
 * - type: "main" | "neutral" (default: "main")
 * - kind: "enable" | "disable" (default: "enable")
 * - children: текст кнопки
 * - onClick: обработчик клика
 * - className: дополнительные классы
 * - disabled: принудительный disabled (перекрывает kind)
 *
 * Размеры из Figma:
 * - Высота: 54px
 * - Border radius: 999px (pill)
 * - Padding: 16px горизонтально
 * - Font: SF Pro Display Medium 510, 16px
 */

function Button({
  variant,
  type: buttonType = 'button',
  kind = 'enable',
  size = 'regular',
  children,
  onClick,
  className = '',
  disabled = false,
  loading = false,
  ...props
}) {
  // `type="main|neutral"` is retained for existing Figma call sites. Native
  // button types remain available through `type="submit|reset|button"`.
  const resolvedVariant = variant || (buttonType === 'neutral' ? 'neutral' : 'main')
  const nativeType = ['main', 'neutral'].includes(buttonType) ? 'button' : buttonType
  const isDisabled = disabled || loading || kind === 'disable'

  const handleClick = (e) => {
    if (isDisabled) return
    safeButtonHaptic()
    onClick?.(e)
  }

  const baseStyles = size === 'micro'
    ? 'inline-flex h-[20px] items-center justify-center rounded-[999px] border px-2 font-[510] text-[12px] leading-[14px] whitespace-nowrap transition-all duration-[160ms] ease-out touch-action-manipulation select-none active:scale-[0.94]'
    : size === 'action'
    ? 'flex h-[60px] w-full flex-col items-center justify-center gap-1 rounded-[999px] border px-2 font-[510] text-[11px] leading-[13px] transition-all duration-[160ms] ease-out touch-action-manipulation select-none active:scale-[0.98]'
    : 'inline-flex h-[54px] w-full items-center justify-center rounded-[999px] border px-6 font-[510] text-[16px] leading-[22px] transition-all duration-[160ms] ease-out touch-action-manipulation select-none'

  // Variant styles
  const variantStyles = size === 'micro'
      ? {
        main: isDisabled
          ? 'border-[var(--figma-text-50)] bg-[var(--figma-accent)] opacity-46 cursor-not-allowed text-[var(--figma-text)]'
          : 'border-[var(--figma-text-50)] bg-[var(--figma-accent)] hover:bg-[var(--figma-accent-pressed)] text-[var(--figma-text)] cursor-pointer',
        neutral: isDisabled
          ? 'border-[var(--figma-border-10)] bg-[var(--figma-surface-08)] opacity-46 cursor-not-allowed text-[var(--figma-text)]'
          : 'border-[var(--figma-border-10)] bg-[var(--figma-surface-08)] hover:bg-[var(--figma-surface-10)] text-[var(--figma-text)] cursor-pointer',
      }
    : {
        main: isDisabled
          ? 'border-[var(--figma-text-50)] bg-[var(--figma-accent)] opacity-46 cursor-not-allowed text-[var(--figma-text)]'
          : 'border-[var(--figma-text-50)] bg-[var(--figma-accent)] hover:bg-[var(--figma-accent-pressed)] active:scale-[0.98] text-[var(--figma-text)] cursor-pointer',
        neutral: isDisabled
          ? 'border-[var(--figma-border-10)] bg-[var(--figma-surface-08)] opacity-46 cursor-not-allowed text-[var(--figma-text)]'
          : 'border-[var(--figma-border-10)] bg-[var(--figma-surface-08)] hover:bg-[var(--figma-surface-10)] active:scale-[0.98] text-[var(--figma-text)] cursor-pointer',
      }

  return (
    <button
      type={nativeType}
      onClick={handleClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={`${baseStyles} ${variantStyles[resolvedVariant] || variantStyles.main} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

export default memo(Button)
